import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useTasksContext } from '../../../../shared/contexts/TasksContext';
import { useImportPopupRef } from '../../../../shared/contexts/ImportPopupContext';
import { useAuth } from '../../../../features/auth/contexts/AuthContext';
import { getExampleLists } from '../../../../shared/utils/presets';
import { HIDE_EXAMPLE_LISTS_WHEN_LOGGED_OUT } from '../../../../shared/constants/featureFlags';
import { exportTasksToText } from '../../../../features/tasks/utils/taskImportExport';
import { calculateOrderForParent, getFileLeafTaskStats } from '../../../../features/tasks/utils/taskUtils';
import { formatFileCreatedDate, shouldShowFileCreatedDate } from '../../utils/fileStateHelpers';
import { trackEvent } from '../../../../shared/utils/analytics';
import ProgressIndicator from '../../../../features/tasks/components/TaskRow/ProgressIndicator/ProgressIndicator';
import SignupLoginModal from '../../../../features/auth/components/SignupLoginModal/SignupLoginModal';
import ImportPopup from '../ImportPopup/ImportPopup';
import ToolbarStyleTooltip from '../../../../ui/components/ToolbarStyleTooltip/ToolbarStyleTooltip';
import './Sidebar.css';

function Sidebar({ isMobileOpen, onMobileClose }) {
  const navigate = useNavigate();
  const { files, currentFileId, presetSlug, switchFile, createFile, updateFileName, toggleArchiveFile, updateFileOrders, updateFileArchiveStatus, fetchFiles, tasks, createTask, fetchTasks, getCachedTasksForFile, openExamplePreset } = useTasksContext();
  const { isLoggedIn } = useAuth();
  const openImportPopupRef = useImportPopupRef();
  const [isCreatingFile, setIsCreatingFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const newFileCreateInFlightRef = useRef(false);
  const isCreatingFileRef = useRef(isCreatingFile);
  const newFileNameRef = useRef(newFileName);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFileName, setEditingFileName] = useState('');
  const [openMenuFileId, setOpenMenuFileId] = useState(null);
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const [dragOverFileId, setDragOverFileId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'before' or 'after'
  const [mouseY, setMouseY] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false); // state kept, menu UI removed (import is now a separate icon)
  const menuRefs = useRef({});
  const sidebarRef = useRef(null);
  const listActionsTooltipAnchorRef = useRef(null);
  const [listActionsTooltipVisible, setListActionsTooltipVisible] = useState(false);
  const [listActionsTooltipText, setListActionsTooltipText] = useState('');
  const showListActionsTooltip = (e, text) => {
    listActionsTooltipAnchorRef.current = e.currentTarget;
    setListActionsTooltipText(text);
    setListActionsTooltipVisible(true);
  };
  const hideListActionsTooltip = () => setListActionsTooltipVisible(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isLoggedIn && isAuthModalOpen) {
      setIsAuthModalOpen(false);
    }
  }, [isLoggedIn, isAuthModalOpen]);

  useEffect(() => {
    isCreatingFileRef.current = isCreatingFile;
  }, [isCreatingFile]);

  useEffect(() => {
    newFileNameRef.current = newFileName;
  }, [newFileName]);

  const requireAuth = useCallback(() => {
    if (isLoggedIn) {
      return true;
    }
    setIsAuthModalOpen(true);
    setOpenMenuFileId(null);
    setIsPlusMenuOpen(false);
    setIsCreatingFile(false);
    setNewFileName('');
    return false;
  }, [isLoggedIn]);
  
  const createNewFileByName = useCallback(async (rawName) => {
    const trimmedName = rawName.trim();
    if (!trimmedName) return;
    if (newFileCreateInFlightRef.current) return; // Prevent double-create (Enter + blur)

    newFileCreateInFlightRef.current = true;

    try {
      const newFile = await createFile(trimmedName);
      setNewFileName('');
      setIsCreatingFile(false);
      if (isLoggedIn && fetchFiles) {
        await fetchFiles();
      }
      if (newFile?.id && isLoggedIn) {
        navigate(`/list/${newFile.id}`, { replace: true });
      }
    } catch (error) {
      alert(`Error creating file: ${error.message}`);
    } finally {
      newFileCreateInFlightRef.current = false;
    }
  }, [createFile, fetchFiles, isLoggedIn, navigate]);

  const handleNewFile = async () => {
    if (!isLoggedIn) {
      trackEvent('logged_out_interaction_new_list_clicked');
    }

    if (isCreatingFile) {
      await createNewFileByName(newFileName);
      return;
    }

    // Show input
    setIsCreatingFile(true);
  };

  const handleNewFileNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleNewFile();
    } else if (e.key === 'Escape') {
      setIsCreatingFile(false);
      setNewFileName('');
    }
  };
  
  const handleNewFileNameBlur = async () => {
    // Only submit when we're currently in "create mode" for this input.
    if (!isCreatingFileRef.current) return;

    const trimmedName = newFileNameRef.current.trim();
    if (!trimmedName) {
      setIsCreatingFile(false);
      setNewFileName('');
      return;
    }

    await createNewFileByName(trimmedName);
  };

  const handleFileClick = (file) => {
    if (editingFileId === file.id) {
      return; // Already editing this file
    }
    if (String(file.id) !== String(currentFileId)) {
      trackEvent('file_opened');
    }
    switchFile(file.id);
    // Close mobile sidebar when a file is selected
    if (onMobileClose) {
      onMobileClose();
    }
  };
  
  const handleFileDoubleClick = (file) => {
    setEditingFileId(file.id);
    setEditingFileName(file.name);
  };
  
  const handleFileNameKeyDown = async (e, fileId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleFileNameSave(fileId);
    } else if (e.key === 'Escape') {
      setEditingFileId(null);
      setEditingFileName('');
    }
  };
  
  const handleFileNameBlur = async (fileId) => {
    await handleFileNameSave(fileId);
  };
  
  const handleFileNameSave = async (fileId) => {
    if (editingFileName.trim()) {
      try {
        await updateFileName(fileId, editingFileName.trim());
      } catch (error) {
        alert(`Error updating file name: ${error.message}`);
      }
    }
    setEditingFileId(null);
    setEditingFileName('');
  };

  const handleMenuToggle = (e, fileId) => {
    e.stopPropagation();
    const newOpenMenuFileId = openMenuFileId === fileId ? null : fileId;
    setOpenMenuFileId(newOpenMenuFileId);
    // Keep hover state when opening menu, clear when closing
    if (!newOpenMenuFileId && hoveredFileId === fileId) {
      setHoveredFileId(null);
    }
  };

  const handleRename = (fileId) => {
    setOpenMenuFileId(null);
    setEditingFileId(fileId);
    const file = files.find(f => f.id === fileId);
    if (file) {
      setEditingFileName(file.name);
    }
  };

  const handleRenameClick = (fileId) => {
    handleRename(fileId);
  };

  const handleExport = async (fileId) => {
    setOpenMenuFileId(null);
    let tasksToExport;
    const isCurrentFile = String(fileId) === String(currentFileId);
    if (!isLoggedIn) {
      if (isCurrentFile) {
        tasksToExport = tasks;
      } else {
        tasksToExport = getCachedTasksForFile ? (getCachedTasksForFile(fileId) || []) : [];
      }
    } else {
      try {
        const response = await fetch(`/api/tasks.php?file_id=${fileId}`, { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to load tasks');
        const data = await response.json();
        tasksToExport = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Export: could not load file tasks', err);
        tasksToExport = [];
      }
    }
    const textContent = exportTasksToText(tasksToExport);
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const file = files.find(f => f.id === fileId);
    const fileName = file ? file.name : 'todolist';
    link.download = `${fileName} - picotodo.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    trackEvent('file_exported', { format: 'txt' });
  };

  const handleExportClick = (fileId) => {
    handleExport(fileId);
  };

  const handleArchive = async (fileId) => {
    setOpenMenuFileId(null);
    try {
      await toggleArchiveFile(fileId);
      if (isLoggedIn && fetchFiles) {
        await fetchFiles();
      }
    } catch (error) {
      alert(`Error archiving file: ${error.message}`);
    }
  };

  const handleArchiveClick = (fileId) => {
    handleArchive(fileId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only check the currently open menu's ref - otherwise clicking a menu item
      // on a non-focused file would close the menu (other refs don't contain the target)
      const openMenuRef = openMenuFileId ? menuRefs.current[openMenuFileId] : null;
      if (openMenuRef && !openMenuRef.contains(event.target)) {
        setOpenMenuFileId(null);
        setHoveredFileId(null);
      }
    };

    if (openMenuFileId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuFileId]);


  // Separate files into regular and archived
  // Handle undefined archived property for backward compatibility
  // Sort by order (NULL values last), then by last_modified
  const regularFiles = useMemo(() => {
    return files
      .filter(file => !file.archived)
      .sort((a, b) => {
        if (a.order === null && b.order === null) {
          return new Date(b.last_modified) - new Date(a.last_modified);
        }
        if (a.order === null) return 1;
        if (b.order === null) return -1;
        return a.order - b.order;
      });
  }, [files]);

  const archivedFiles = useMemo(() => {
    return files
      .filter(file => file.archived === true)
      .sort((a, b) => {
        if (a.order === null && b.order === null) {
          return new Date(b.last_modified) - new Date(a.last_modified);
        }
        if (a.order === null) return 1;
        if (b.order === null) return -1;
        return a.order - b.order;
      });
  }, [files]);

  // Custom modifier to restrict dragging within sidebar (only vertical movement)
  const restrictToSidebar = useCallback(({ transform }) => {
    if (!transform) {
      return transform;
    }
    
    // Only allow vertical movement, restrict horizontal to 0
    return {
      ...transform,
      x: 0,
    };
  }, []);

  // Helper function to calculate drop position - used by both handleDragOver and handleDragEnd
  const calculateDropPosition = useCallback((over, active, currentMouseY) => {
    if (!over || !over.rect) {
      return 'after'; // Default
    }

    const overRect = over.rect;
    const overCenterY = overRect.top + overRect.height / 2;
    
    // Try multiple methods to get pointer position
    let pointerY = null;
    
    // Method 1: Use tracked mouse position (most accurate)
    if (currentMouseY !== null) {
      pointerY = currentMouseY;
    }
    // Method 2: Use active rect top (where the dragged item is)
    else if (active && active.rect) {
      pointerY = active.rect.top;
    }
    
    // Calculate position based on pointer relative to center
    if (pointerY !== null) {
      return pointerY < overCenterY ? 'before' : 'after';
    }
    
    // Fallback
    return 'after';
  }, []);

  // Handle drag end - update file order and archive status
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    // Capture values before clearing state
    const currentMouseY = mouseY;
    const currentDropPosition = dropPosition;
    
    // Clear state
    setDragOverFileId(null);
    setDropPosition(null);
    setMouseY(null);

    // If no drop target, do nothing
    if (!over || !active) {
      return;
    }

    const draggedFileId = active.id;
    const dropTargetFileId = over.id;

    // Don't do anything if dropped on itself
    if (draggedFileId === dropTargetFileId) {
      return;
    }

    // Find the dragged file and drop target file
    const draggedFile = files.find(f => f.id === draggedFileId);
    const dropTargetFile = files.find(f => f.id === dropTargetFileId);

    if (!draggedFile || !dropTargetFile) {
      return;
    }

    // Calculate drop position using the same logic as handleDragOver
    // Prefer the stored dropPosition if available (most recent), otherwise recalculate
    const calculatedPosition = currentDropPosition || calculateDropPosition(over, active, currentMouseY);
    const placeBefore = calculatedPosition === 'before';

    // Determine which section the drop target is in
    const dropTargetIsArchived = dropTargetFile.archived === true;
    const draggedFileIsArchived = draggedFile.archived === true;

    // Get the appropriate file list (regular or archived)
    const targetFileList = dropTargetIsArchived ? archivedFiles : regularFiles;
    
    // Find the index of the drop target in its list
    const dropTargetIndex = targetFileList.findIndex(f => f.id === dropTargetFileId);
    
    if (dropTargetIndex === -1) {
      return;
    }

    // Get all files in the target section (excluding the dragged file)
    const filesInTargetSection = targetFileList.filter(f => f.id !== draggedFileId);
    
    // Find the drop target in the filtered list
    const dropTargetInFiltered = filesInTargetSection.findIndex(f => f.id === dropTargetFileId);
    
    if (dropTargetInFiltered === -1) {
      return;
    }

    // Build new file order list: insert dragged file before or after drop target
    const fileOrders = [];
    let orderCounter = 1;
    
    filesInTargetSection.forEach((file, index) => {
      if (file.id === dropTargetFileId) {
        if (placeBefore) {
          // Insert dragged file before drop target
          fileOrders.push({ id: draggedFileId, order: orderCounter++ });
          fileOrders.push({ id: file.id, order: orderCounter++ });
        } else {
          // Insert dragged file after drop target
          fileOrders.push({ id: file.id, order: orderCounter++ });
          fileOrders.push({ id: draggedFileId, order: orderCounter++ });
        }
      } else {
        // All other files get sequential orders
        fileOrders.push({ id: file.id, order: orderCounter++ });
      }
    });

    // If we need to update archive status, do that first
    if (draggedFileIsArchived !== dropTargetIsArchived) {
      try {
        await updateFileArchiveStatus(draggedFileId, dropTargetIsArchived, true);
      } catch (error) {
        console.error('Error updating archive status:', error);
        if (isLoggedIn && fetchFiles) {
          await fetchFiles();
        }
        return;
      }
    }

    // Update all orders
    try {
      await updateFileOrders(fileOrders, true);
      if (isLoggedIn && fetchFiles) {
        await fetchFiles();
      }
    } catch (error) {
      console.error('Error updating file orders:', error);
      if (isLoggedIn && fetchFiles) {
        await fetchFiles();
      }
    }
  }, [files, regularFiles, archivedFiles, updateFileOrders, updateFileArchiveStatus, fetchFiles, dropPosition, mouseY, calculateDropPosition, isLoggedIn]);

  // Track mouse position during drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMouseY(e.clientY);
    };

    // Only track mouse position when dragging
    if (dragOverFileId !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', (e) => {
        if (e.touches && e.touches[0]) {
          setMouseY(e.touches[0].clientY);
        }
      });
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [dragOverFileId]);

  // Handle drag over - track which file is being dragged over and drop position
  const handleDragOver = useCallback((event) => {
    const { over, active } = event;
    if (over) {
      setDragOverFileId(over.id);
      
      // Calculate drop position using the same helper function
      const position = calculateDropPosition(over, active, mouseY);
      setDropPosition(position);
    }
  }, [mouseY, calculateDropPosition]);

  // Handle drag cancel - clear drag over state
  const handleDragCancel = useCallback(() => {
    setDragOverFileId(null);
    setDropPosition(null);
    setMouseY(null);
  }, []);

  // Store drag handle props per file - using a Map to store listeners/attributes for each file
  const dragHandlePropsMap = useRef(new Map());

  // Draggable file item wrapper - provides drag context
  const DraggableFileItem = ({ file, children, isHovered }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id: file.id,
      disabled: false,
    });

    const style = {
      ...(transform ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      } : {}),
      ...(isDragging ? {
        position: 'relative',
        zIndex: 1000,
      } : {}),
    };

    // Store drag handle props in the map
    dragHandlePropsMap.current.set(file.id, {
      listeners: isHovered ? listeners : null,
      attributes: isHovered ? attributes : null,
    });

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={isDragging ? 'file-item-dragging' : ''}
      >
        {children}
      </div>
    );
  };

  // Droppable file item component
  const DroppableFileItem = ({ file, children, isOver, dropPosition }) => {
    const { setNodeRef } = useDroppable({
      id: file.id,
      disabled: false,
    });

    return (
      <div ref={setNodeRef}>
        {children}
      </div>
    );
  };

  // Helper component for file item with progress indicator
  const FileItemWithProgress = ({ file, files, tasks, isActive, onFileClick, onFileDoubleClick, isHovered, isLoggedIn, getCachedTasksForFile, linkTo, linkState, onLinkClick, isMobile, onNavigate }) => {
    // Get drag handle props from the map
    const dragHandleProps = dragHandlePropsMap.current.get(file.id);
    const dragHandleListeners = dragHandleProps?.listeners || null;
    const dragHandleAttributes = dragHandleProps?.attributes || null;
    
    // Use refs to maintain previous values during transition to prevent jumping
    const prevFileStatsRef = useRef(null);
    const prevVirtualTaskRef = useRef(null);
    
    // For active file: use real-time progress from tasks once they're loaded for this file.
    // During switch transition (tasks still from previous file), use saved_progress_status to avoid blink.
    // For inactive files: use saved progress status
    const fileStats = useMemo(() => {
      if (isActive) {
        if (!tasks || !file.id) {
          return prevFileStatsRef.current;
        }
        // During transition after switch: tasks may still belong to the previous file; use saved state to avoid blink
        const tasksBelongToThisFile = tasks.length === 0 || tasks.every((t) => t.file_id === file.id);
        if (!tasksBelongToThisFile && file.saved_progress_status) {
          const stats = {
            completed: file.saved_progress_status.completed || 0,
            total: file.saved_progress_status.total || 0,
            weightedProgress: file.saved_progress_status.weightedProgress || 0
          };
          prevFileStatsRef.current = stats;
          return stats;
        }
        if (!tasksBelongToThisFile) {
          return prevFileStatsRef.current;
        }
        // Tasks belong to this file: use live stats
        const stats = getFileLeafTaskStats(tasks, file.id);
        
        // If there are no tasks, check if there's a placeholder (empty unsaved task)
        if (stats && stats.total === 0) {
          const fileTasks = tasks.filter(task => task.file_id === file.id);
          if (fileTasks.length === 0) {
            const placeholderStats = { completed: 0, total: 1, weightedProgress: 0 };
            prevFileStatsRef.current = placeholderStats;
            return placeholderStats;
          }
        }
        
        if (stats) {
          prevFileStatsRef.current = stats;
        }
        return stats || prevFileStatsRef.current;
      } else {
        // Inactive file: use saved progress status, or when logged out use locally cached tasks
        if (!file.saved_progress_status) {
          if (!isLoggedIn && getCachedTasksForFile) {
            const localTasks = getCachedTasksForFile(file.id);
            if (Array.isArray(localTasks)) {
              const stats = getFileLeafTaskStats(localTasks, file.id);
              if (stats && stats.total === 0) {
                const fileTasks = localTasks.filter(task => task.file_id === file.id);
                if (fileTasks.length === 0) {
                  const placeholderStats = { completed: 0, total: 1, weightedProgress: 0 };
                  prevFileStatsRef.current = placeholderStats;
                  return placeholderStats;
                }
              }
              if (stats) {
                prevFileStatsRef.current = stats;
                return stats;
              }
            }
          }
          // New file with no saved progress (e.g. created then switched away): show 1 incomplete task (empty circle)
          const placeholderStats = { completed: 0, total: 1, weightedProgress: 0 };
          prevFileStatsRef.current = placeholderStats;
          return placeholderStats;
        }
        const stats = {
          completed: file.saved_progress_status.completed || 0,
          total: file.saved_progress_status.total || 0,
          weightedProgress: file.saved_progress_status.weightedProgress || 0
        };
        prevFileStatsRef.current = stats;
        return stats;
      }
    }, [tasks, file.id, file.saved_progress_status, isActive, isLoggedIn, getCachedTasksForFile]);

    // Create a virtual task object with leaf tasks as subtasks for ProgressIndicator
    const virtualTask = useMemo(() => {
      if (!fileStats) {
        return prevVirtualTaskRef.current;
      }
      
      if (isActive) {
        if (!tasks || !file.id) {
          return prevVirtualTaskRef.current;
        }
        // During transition: use saved status breakdown so indicator matches fileStats (no blink)
        const tasksBelongToThisFile = tasks.length === 0 || tasks.every((t) => t.file_id === file.id);
        if (!tasksBelongToThisFile && file.saved_progress_status?.statusBreakdown) {
          const statusBreakdown = file.saved_progress_status.statusBreakdown;
          const leafTasks = [];
          statusBreakdown.forEach(({ status, count }) => {
            for (let i = 0; i < count; i++) {
              leafTasks.push({
                id: `saved-active-${file.id}-${status}-${i}`,
                status: status,
                subtasks: []
              });
            }
          });
          const task = {
            id: `file-virtual-task-${file.id}-saved`,
            subtasks: leafTasks
          };
          prevVirtualTaskRef.current = task;
          return task;
        }
        if (!tasksBelongToThisFile) {
          return prevVirtualTaskRef.current;
        }
        
        // Tasks belong to this file: build from actual tasks
        const fileTasks = tasks.filter(task => task.file_id === file.id);
        
        if (fileTasks.length === 0 && fileStats.total === 1) {
          const task = {
            id: `file-virtual-task-${file.id}`,
            subtasks: [{
              id: 'placeholder',
              text: '',
              status: 'incomplete',
              subtasks: []
            }]
          };
          prevVirtualTaskRef.current = task;
          return task;
        }
        
        if (fileStats.total === 0) {
          return prevVirtualTaskRef.current;
        }
        
        const parentTaskIds = new Set();
        fileTasks.forEach(task => {
          if (task.parent_task_id) {
            parentTaskIds.add(task.parent_task_id);
          }
        });
        const leafTasks = fileTasks
          .filter(task => !parentTaskIds.has(task.id))
          .map(task => ({ ...task, subtasks: [] }));
        
        const task = {
          id: `file-virtual-task-${file.id}`,
          subtasks: leafTasks
        };
        prevVirtualTaskRef.current = task;
        return task;
      } else {
        // Inactive file: create virtual task from saved progress status, or from locally cached tasks when logged out
        if (!file.saved_progress_status || !file.saved_progress_status.statusBreakdown) {
          // Placeholder stats (0/1): new empty file — show one incomplete subtask so empty circle renders
          if (fileStats && fileStats.total === 1) {
            const task = {
              id: `file-virtual-task-${file.id}`,
              subtasks: [{ id: 'placeholder', text: '', status: 'incomplete', subtasks: [] }]
            };
            prevVirtualTaskRef.current = task;
            return task;
          }
          // Logged out: use cached tasks for this file to build virtual task
          if (!isLoggedIn && getCachedTasksForFile && fileStats) {
            const localTasks = getCachedTasksForFile(file.id);
            if (Array.isArray(localTasks) && file.id) {
              const fileTasks = localTasks.filter(task => task.file_id === file.id);
              if (fileTasks.length === 0 && fileStats.total === 1) {
                const task = {
                  id: `file-virtual-task-${file.id}`,
                  subtasks: [{ id: 'placeholder', text: '', status: 'incomplete', subtasks: [] }]
                };
                prevVirtualTaskRef.current = task;
                return task;
              }
              if (fileStats.total === 0) {
                return prevVirtualTaskRef.current;
              }
              const parentTaskIds = new Set();
              fileTasks.forEach(task => {
                if (task.parent_task_id) parentTaskIds.add(task.parent_task_id);
              });
              const leafTasks = fileTasks
                .filter(task => !parentTaskIds.has(task.id))
                .map(task => ({ ...task, subtasks: [] }));
              const task = {
                id: `file-virtual-task-${file.id}`,
                subtasks: leafTasks
              };
              prevVirtualTaskRef.current = task;
              return task;
            }
          }
          return prevVirtualTaskRef.current;
        }
        
        // Create virtual task with status breakdown for rendering
        const statusBreakdown = file.saved_progress_status.statusBreakdown;
        const leafTasks = [];
        
        statusBreakdown.forEach(({ status, count }) => {
          for (let i = 0; i < count; i++) {
            leafTasks.push({
              id: `saved-${file.id}-${status}-${i}`,
              status: status,
              subtasks: []
            });
          }
        });
        
        const task = {
          id: `file-virtual-task-${file.id}-saved`,
          subtasks: leafTasks
        };
        prevVirtualTaskRef.current = task;
        return task;
      }
    }, [tasks, file.id, file.saved_progress_status, fileStats, isActive, isLoggedIn, getCachedTasksForFile]);

    // Helper functions for ProgressIndicator (not used for file-level, but required by component)
    const getSubtaskStatusColor = () => null;
    const getSubtaskStatusBorderColor = () => null;

    const fileItemClassName = `file-item text-non-selectable ${isActive ? 'active' : ''} ${file.archived ? 'archived' : ''}`;
    const sharedContent = (
      <>
        {isHovered && (
          <span
            className="file-drag-handle"
            {...(dragHandleListeners || {})}
            {...(dragHandleAttributes || {})}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <svg width="4" height="12" viewBox="0 0 4 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="2" cy="2" r="1" fill="currentColor"/>
              <circle cx="2" cy="6" r="1" fill="currentColor"/>
              <circle cx="2" cy="10" r="1" fill="currentColor"/>
            </svg>
          </span>
        )}
        <span className={`file-item-progress-indicator ${file.archived ? 'archived' : ''}`}>
          {virtualTask && fileStats && (
            <ProgressIndicator
              task={virtualTask}
              tasks={isActive ? tasks : ((!isLoggedIn && getCachedTasksForFile && getCachedTasksForFile(file.id)) || [])}
              subtaskStats={fileStats}
              isSequentialDisabled={false}
              isBlockingTaskHighlighted={false}
              isBlockingTaskNotBlocked={false}
              onBlockedIconMouseEnter={undefined}
              onBlockedIconMouseLeave={undefined}
              summaryLabel="File task status"
              getSubtaskStatusColor={getSubtaskStatusColor}
              getSubtaskStatusBorderColor={getSubtaskStatusBorderColor}
              propagateClick
            />
          )}
        </span>
        {file.name}
        {shouldShowFileCreatedDate(files, file.name) && file.created_at && (
          <span className="file-item-created-date">{formatFileCreatedDate(file.created_at)}</span>
        )}
      </>
    );

    if (linkTo) {
      // On mobile: use button + programmatic navigation so a single tap switches file (avoids two-tap link behavior).
      // Opening in a new tab is not available from the file list on mobile, so we never switch the current tab from a "new tab" action.
      if (isMobile && onNavigate) {
        return (
          <button
            type="button"
            className={fileItemClassName}
            onClick={(e) => {
              if (e?.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                onLinkClick?.(file, e);
                onNavigate(linkTo, linkState);
              }
            }}
            onDoubleClick={(e) => {
              e.preventDefault();
              onFileDoubleClick(file);
            }}
          >
            {sharedContent}
          </button>
        );
      }
      return (
        <Link
          to={linkTo}
          state={linkState}
          className={fileItemClassName}
          onClick={(e) => onLinkClick?.(file, e)}
          onDoubleClick={(e) => {
            e.preventDefault();
            onFileDoubleClick(file);
          }}
        >
          {sharedContent}
        </Link>
      );
    }

    return (
      <button
        className={fileItemClassName}
        onClick={() => onFileClick(file)}
        onDoubleClick={() => onFileDoubleClick(file)}
      >
        {sharedContent}
      </button>
    );
  };

  // Example list item with progress (only when that example has been opened; persists when user switches away)
  const ExampleListItemWithProgress = ({ slug, name, tasks, currentFileId, presetSlug, getCachedTasksForFile }) => {
    const fileId = `preset:${slug}`;
    const isActive = currentFileId === fileId || (presetSlug === slug && currentFileId === 'preset');
    const tasksForFile = isActive
      ? (tasks || []).filter((t) => t.file_id === fileId)
      : (getCachedTasksForFile && getCachedTasksForFile(fileId)) || [];
    const fileStats = useMemo(
      () => (tasksForFile.length > 0 ? getFileLeafTaskStats(tasksForFile, fileId) : null),
      [tasksForFile, fileId]
    );
    const virtualTask = useMemo(() => {
      if (!fileStats || fileStats.total === 0) return null;
      const fileTasks = tasksForFile;
      if (fileTasks.length === 0 && fileStats.total === 1) {
        return {
          id: `file-virtual-task-${fileId}`,
          subtasks: [{ id: 'placeholder', text: '', status: 'incomplete', subtasks: [] }]
        };
      }
      const parentTaskIds = new Set();
      fileTasks.forEach((task) => {
        if (task.parent_task_id) parentTaskIds.add(task.parent_task_id);
      });
      const leafTasks = fileTasks
        .filter((task) => !parentTaskIds.has(task.id))
        .map((task) => ({ ...task, subtasks: [] }));
      return { id: `file-virtual-task-${fileId}`, subtasks: leafTasks };
    }, [tasksForFile, fileId, fileStats]);
    const getSubtaskStatusColor = () => null;
    const getSubtaskStatusBorderColor = () => null;

    return (
      <div className="file-item-wrapper text-non-selectable">
        <button
          type="button"
          className={`file-item text-non-selectable ${isActive ? 'active' : ''}`}
          onClick={() => {
            openExamplePreset(slug);
            if (onMobileClose) onMobileClose();
          }}
        >
          {virtualTask && fileStats && fileStats.total > 0 ? (
            <span className="file-item-progress-indicator" aria-hidden="true">
              <ProgressIndicator
                task={virtualTask}
                tasks={isActive ? tasks || [] : (tasksForFile || [])}
                subtaskStats={fileStats}
                isSequentialDisabled={false}
                isBlockingTaskHighlighted={false}
                isBlockingTaskNotBlocked={false}
                onBlockedIconMouseEnter={undefined}
                onBlockedIconMouseLeave={undefined}
                summaryLabel="File task status"
                getSubtaskStatusColor={getSubtaskStatusColor}
                getSubtaskStatusBorderColor={getSubtaskStatusBorderColor}
                propagateClick
              />
            </span>
          ) : null}
          {name}
        </button>
      </div>
    );
  };
  
  return (
    <>
      {isMobileOpen && <div className="sidebar-overlay" onClick={onMobileClose}></div>}
      <aside ref={sidebarRef} className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo-container">
        <div className="sidebar-logo">
          <svg width="130" height="40" viewBox="0 0 130 40" xmlns="http://www.w3.org/2000/svg">
            <text x="10" y="28" fontFamily="system-ui, -apple-system, sans-serif" fontSize="24" fontWeight="600">
              <tspan fill="#444" fontSize="24">pico</tspan><tspan fill="#333" fontSize="28" dx="1">todo</tspan>
            </text>
          </svg>
          <span className="logo-badge">BETA</span>
        </div>
      </div>
      <div className="sidebar-section-header">
        <div className="sidebar-section-title-wrapper">
          <span className="sidebar-section-title">Lists</span>
        </div>
        <div className="sidebar-section-actions">
          <div className="sidebar-plus-button-container">
            <ImportPopup
              renderTrigger={({ open }) => {
                if (openImportPopupRef) openImportPopupRef.current = open;
                return (
                <button
                  type="button"
                  className="sidebar-import-button text-non-selectable"
                  onClick={open}
                  aria-label="Import list"
                  onMouseEnter={(e) => showListActionsTooltip(e, 'Import list')}
                  onMouseLeave={hideListActionsTooltip}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <g fill="none" fillRule="evenodd">
                      <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z"/>
                      <path fill="currentColor" d="M13.586 2a2 2 0 0 1 1.284.467l.13.119L19.414 7a2 2 0 0 1 .578 1.238l.008.176V20a2 2 0 0 1-1.85 1.995L18 22h-6v-2h6V10h-4.5a1.5 1.5 0 0 1-1.493-1.356L12 8.5V4H6v8H4V4a2 2 0 0 1 1.85-1.995L6 2zM7.707 14.465l2.829 2.828a1 1 0 0 1 0 1.414l-2.829 2.828a1 1 0 1 1-1.414-1.414L7.414 19H3a1 1 0 1 1 0-2h4.414l-1.121-1.121a1 1 0 1 1 1.414-1.415ZM14 4.414V8h3.586z"/>
                    </g>
                  </svg>
                </button>
                );
              }}
            />
            <button
              className="sidebar-new-file-button text-non-selectable"
              onClick={handleNewFile}
              aria-label="New list"
              onMouseEnter={(e) => showListActionsTooltip(e, 'New list')}
              onMouseLeave={hideListActionsTooltip}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 1V9M1 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <ToolbarStyleTooltip
              anchorRef={listActionsTooltipAnchorRef}
              visible={listActionsTooltipVisible}
              text={listActionsTooltipText}
              placement="bottom"
              portal
            />
          </div>
        </div>
      </div>
      <DndContext 
        modifiers={[restrictToSidebar]}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragCancel={handleDragCancel}
      >
      <div className="sidebar-content">
            <div className="file-list" data-container="lists" id="lists-list">
          {isCreatingFile && (
            <div className="file-item-wrapper">
              <input
                type="text"
                className="file-item-input"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={handleNewFileNameKeyDown}
                onBlur={handleNewFileNameBlur}
                placeholder="New list..."
                autoFocus
              />
            </div>
          )}
          {regularFiles.map(file => (
            <div 
              key={file.id} 
              className={`file-item-wrapper text-non-selectable ${
                dragOverFileId === file.id && dropPosition 
                  ? `file-item-drop-${dropPosition}` 
                  : ''
              }`}
            >
              {editingFileId === file.id ? (
                <input
                  type="text"
                  className="file-item-input"
                  value={editingFileName}
                  onChange={(e) => setEditingFileName(e.target.value)}
                  onKeyDown={(e) => handleFileNameKeyDown(e, file.id)}
                  onBlur={() => handleFileNameBlur(file.id)}
                  autoFocus
                />
              ) : (
                <DroppableFileItem 
                  file={file} 
                  isOver={dragOverFileId === file.id}
                  dropPosition={dragOverFileId === file.id ? dropPosition : null}
                >
                  <DraggableFileItem 
                    file={file}
                    isHovered={hoveredFileId === file.id || file.id === currentFileId}
                  >
                <div 
                  className={`file-item-container ${file.id === currentFileId ? 'active' : ''} ${(file.id === currentFileId || hoveredFileId === file.id || openMenuFileId === file.id) ? 'menu-visible' : ''}`}
                  onMouseEnter={() => setHoveredFileId(file.id)}
                  onMouseLeave={() => setHoveredFileId(null)}
                >
                  <FileItemWithProgress
                    file={file}
                    files={files}
                    tasks={tasks}
                    isActive={file.id === currentFileId}
                    onFileClick={handleFileClick}
                    onFileDoubleClick={handleFileDoubleClick}
                    isHovered={hoveredFileId === file.id || file.id === currentFileId}
                    isLoggedIn={isLoggedIn}
                    getCachedTasksForFile={getCachedTasksForFile}
                    linkTo={isLoggedIn && editingFileId !== file.id ? `/list/${file.id}` : undefined}
                    linkState={isLoggedIn && editingFileId !== file.id ? { fromSidebar: true } : undefined}
                    onLinkClick={(clickedFile, e) => {
                      // Only switch (and save previous file progress) for same-tab navigation; skip for Cmd/Ctrl+click or middle-click (new tab)
                      if (e?.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                        switchFile(clickedFile.id);
                      }
                      onMobileClose?.();
                    }}
                    isMobile={isMobileViewport}
                    onNavigate={(to, state) => navigate(to, { state })}
                  />
                  {(file.id === currentFileId || hoveredFileId === file.id || openMenuFileId === file.id) && (
                    <div 
                      className="file-item-dots-menu-container" 
                      ref={el => menuRefs.current[file.id] = el}
                      onMouseEnter={() => setHoveredFileId(file.id)}
                      onMouseLeave={() => {
                        // Only clear hover if menu is not open
                        if (openMenuFileId !== file.id) {
                          setHoveredFileId(null);
                        }
                      }}
                    >
                      <button 
                        className="file-item-dots-menu-button text-non-selectable" 
                        onClick={(e) => handleMenuToggle(e, file.id)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        title="Menu"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="4" cy="8" r="1.5" fill="currentColor"/>
                          <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                          <circle cx="12" cy="8" r="1.5" fill="currentColor"/>
                        </svg>
                      </button>
                      {openMenuFileId === file.id && (
                        <div className="file-item-dots-submenu text-non-selectable">
                          <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleRenameClick(file.id)}>Rename</div>
                          <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleExportClick(file.id)}>Export</div>
                          <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleArchiveClick(file.id)}>
                            {file.archived ? 'Unarchive' : 'Archive'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                  </DraggableFileItem>
                </DroppableFileItem>
              )}
            </div>
          ))}
        </div>
        {!isLoggedIn && !presetSlug && !HIDE_EXAMPLE_LISTS_WHEN_LOGGED_OUT && getExampleLists().length > 0 && (
          <>
            <div className="sidebar-section-header">
              <div className="sidebar-section-title-wrapper">
                <span className="sidebar-section-title">Example lists</span>
              </div>
            </div>
            <div className="sidebar-content">
              <div className="file-list" data-container="example-lists" id="example-lists">
                {getExampleLists().map(({ slug, name }) => (
                  <ExampleListItemWithProgress
                    key={slug}
                    slug={slug}
                    name={name}
                    tasks={tasks}
                    currentFileId={currentFileId}
                    presetSlug={presetSlug}
                    getCachedTasksForFile={getCachedTasksForFile}
                  />
                ))}
              </div>
            </div>
          </>
        )}
        <>
        <div className="sidebar-section-header">
          <div className="sidebar-section-title-wrapper">
            <span className="sidebar-section-title">Archived</span>
          </div>
        </div>
        {archivedFiles.length > 0 ? (
          <div className="sidebar-content">
              <div className="file-list" data-container="archived" id="archived-list">
              {archivedFiles.map(file => (
                <div 
                  key={file.id} 
                  className={`file-item-wrapper text-non-selectable ${
                    dragOverFileId === file.id && dropPosition 
                      ? `file-item-drop-${dropPosition}` 
                      : ''
                  }`}
                >
                  {editingFileId === file.id ? (
                    <input
                      type="text"
                      className="file-item-input"
                      value={editingFileName}
                      onChange={(e) => setEditingFileName(e.target.value)}
                      onKeyDown={(e) => handleFileNameKeyDown(e, file.id)}
                      onBlur={() => handleFileNameBlur(file.id)}
                      autoFocus
                    />
                  ) : (
                    <DroppableFileItem 
                      file={file} 
                      isOver={dragOverFileId === file.id}
                      dropPosition={dragOverFileId === file.id ? dropPosition : null}
                    >
                      <DraggableFileItem 
                        file={file}
                        isHovered={hoveredFileId === file.id || file.id === currentFileId}
                      >
                    <div 
                      className={`file-item-container ${file.id === currentFileId ? 'active' : ''} ${(file.id === currentFileId || hoveredFileId === file.id || openMenuFileId === file.id) ? 'menu-visible' : ''}`}
                      onMouseEnter={() => setHoveredFileId(file.id)}
                      onMouseLeave={() => setHoveredFileId(null)}
                    >
                        <FileItemWithProgress
                        file={file}
                        files={files}
                        tasks={tasks}
                        isActive={file.id === currentFileId}
                        onFileClick={handleFileClick}
                        onFileDoubleClick={handleFileDoubleClick}
                        isHovered={hoveredFileId === file.id || file.id === currentFileId}
                        isLoggedIn={isLoggedIn}
                        getCachedTasksForFile={getCachedTasksForFile}
                        linkTo={isLoggedIn && editingFileId !== file.id ? `/list/${file.id}` : undefined}
                        linkState={isLoggedIn && editingFileId !== file.id ? { fromSidebar: true } : undefined}
                    onLinkClick={(clickedFile, e) => {
                      // Only switch (and save previous file progress) for same-tab navigation; skip for Cmd/Ctrl+click or middle-click (new tab)
                      if (e?.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
                        switchFile(clickedFile.id);
                      }
                      onMobileClose?.();
                    }}
                    isMobile={isMobileViewport}
                    onNavigate={(to, state) => navigate(to, { state })}
                  />
                  {(file.id === currentFileId || hoveredFileId === file.id || openMenuFileId === file.id) && (
                    <div 
                      className="file-item-dots-menu-container"
                          ref={el => menuRefs.current[file.id] = el}
                          onMouseEnter={() => setHoveredFileId(file.id)}
                          onMouseLeave={() => {
                            // Only clear hover if menu is not open
                            if (openMenuFileId !== file.id) {
                              setHoveredFileId(null);
                            }
                          }}
                        >
                          <button 
                            className="file-item-dots-menu-button text-non-selectable" 
                            onClick={(e) => handleMenuToggle(e, file.id)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            title="Menu"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="4" cy="8" r="1.5" fill="currentColor"/>
                              <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                              <circle cx="12" cy="8" r="1.5" fill="currentColor"/>
                            </svg>
                          </button>
                          {openMenuFileId === file.id && (
                            <div className="file-item-dots-submenu text-non-selectable">
                              <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleRenameClick(file.id)}>Rename</div>
                              <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleExportClick(file.id)}>Export</div>
                              <div className="file-item-dots-submenu-item text-non-selectable" onClick={() => handleArchiveClick(file.id)}>
                                {file.archived ? 'Unarchive' : 'Archive'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                      </DraggableFileItem>
                    </DroppableFileItem>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="sidebar-content">
            <div className="file-list-empty">No archived lists</div>
          </div>
        )}
        </>
      </div>
      </DndContext>
    </aside>
      <SignupLoginModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        tasks={[]}
        initialMode="signup"
        source="sidebar"
      />
    </>
  );
}

export default Sidebar;



